import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Box,
  Typography,
  CircularProgress,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip,
  Fade,
  Slide,
  IconButton,
  Card,
  CardContent,
  LinearProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import {
  Warning as WarningIcon,
  Close as CloseIcon,
  LocationOn as LocationIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Message as MessageIcon,
  AppRegistration as AppIcon,
} from '@mui/icons-material';
import { supabase } from '../lib/supabaseClient';

interface EmergencyAlertTriggerProps {
  variant?: 'contained' | 'outlined' | 'text';
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
  sx?: SxProps<Theme>;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export default function EmergencyAlertTrigger({
  variant = 'contained',
  size = 'medium',
  fullWidth = false,
  sx = {},
  open: externalOpen,
  onOpenChange,
}: EmergencyAlertTriggerProps) {
  const [internalDialogOpen, setInternalDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [notes, setNotes] = useState('');
  const [currentStep, setCurrentStep] = useState(0);
  const [locationStatus, setLocationStatus] = useState<'idle' | 'getting' | 'success' | 'error'>('idle');
  const [userStatus, setUserStatus] = useState<'idle' | 'getting' | 'success' | 'error'>('idle');
  const [shareToWhatsApp, setShareToWhatsApp] = useState(false);

  // Use external open state if provided, otherwise use internal state
  const dialogOpen = externalOpen !== undefined ? externalOpen : internalDialogOpen;
  const setDialogOpen = (open: boolean) => {
    if (externalOpen !== undefined) {
      onOpenChange?.(open);
    } else {
      setInternalDialogOpen(open);
    }
    
    if (!open) {
      // Reset states when dialog closes
      setCurrentStep(0);
      setLocationStatus('idle');
      setUserStatus('idle');
      setError('');
      setSuccess('');
      setNotes('');
    }
  };

  const getCurrentLocation = (): Promise<{ latitude: number; longitude: number; address: string }> => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by this browser'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            // Get address from coordinates
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${position.coords.latitude}&lon=${position.coords.longitude}&zoom=18&addressdetails=1`
            );
            const data = await response.json();
            const address = data.display_name || 'Unknown location';

            resolve({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              address: address,
            });
          } catch {
            resolve({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              address: 'Unknown location',
            });
          }
        },
        () => {
          reject(new Error('Unable to get current location. Please check your location permissions.'));
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 30000,
        }
      );
    });
  };

  const createWhatsAppMessage = (
    username: string,
    userGroup: string,
    latitude: number,
    longitude: number,
    address: string,
    remarks?: string
  ): string => {
    const timestamp = new Date().toLocaleString();
    
    let message = '🚨 *EMERGENCY ALERT* 🚨\n\n';
    message += `*User:* ${username} (${userGroup})\n`;
    message += `*Time:* ${timestamp}\n\n`;
    message += '*📍 Location Details:*\n';
    message += `• Address: ${address}\n`;
    message += `• Coordinates: ${latitude}, ${longitude}\n`;
    message += `• Google Maps: https://maps.google.com/?q=${latitude},${longitude}\n\n`;
    
    if (remarks && remarks.trim()) {
      message += '*📝 Remarks:*\n' + remarks + '\n\n';
    }
    
    message += '*⚠️ This is an emergency alert sent via OneMove web app.*\n';
    message += '*Please respond immediately if you are nearby.*';
    
    return message;
  };

  const shareToWhatsAppFunction = async (
    username: string,
    userGroup: string,
    latitude: number,
    longitude: number,
    address: string,
    remarks?: string
  ) => {
    try {
      const message = createWhatsAppMessage(username, userGroup, latitude, longitude, address, remarks);
      const encodedMessage = encodeURIComponent(message);
      
      // Try WhatsApp Web first
      const whatsappUrl = `https://wa.me/?text=${encodedMessage}`;
      window.open(whatsappUrl, '_blank');
      
      // Fallback: Copy to clipboard
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(message);
        console.log('Message copied to clipboard as fallback');
      }
    } catch (error) {
      console.error('Error sharing to WhatsApp:', error);
      // Fallback: Copy to clipboard
      try {
        const message = createWhatsAppMessage(username, userGroup, latitude, longitude, address, remarks);
        await navigator.clipboard.writeText(message);
        console.log('Message copied to clipboard as fallback');
      } catch (clipboardError) {
        console.error('Error copying to clipboard:', clipboardError);
      }
    }
  };

  const handleEmergencyAlert = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    setCurrentStep(1);

    try {
      // Step 1: Get current location
      setLocationStatus('getting');
      const location = await getCurrentLocation();
      setLocationStatus('success');
      setCurrentStep(2);

      // Step 2: Get user data
      setUserStatus('getting');
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      const { data: userData } = await supabase
        .from('users')
        .select('username, user_group, user_role')
        .eq('id', user.id)
        .single();

      if (!userData) {
        throw new Error('User data not found');
      }
      setUserStatus('success');
      setCurrentStep(3);

      // Step 3: Create emergency alert
      const { error: alertError } = await supabase
        .from('emergency_alerts')
        .insert({
          user_id: user.id,
          username: userData.username,
          user_group: userData.user_group,
          latitude: location.latitude,
          longitude: location.longitude,
          address: location.address,
          status: 'active',
          notes: notes || null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });

      if (alertError) {
        throw new Error(alertError.message);
      }

      // Step 4: Share to WhatsApp if selected
      if (shareToWhatsApp) {
        await shareToWhatsAppFunction(
          userData.username,
          userData.user_group,
          location.latitude,
          location.longitude,
          location.address,
          notes || undefined
        );
      }

      setSuccess(shareToWhatsApp 
        ? 'Emergency alert sent successfully! WhatsApp opened for sharing.' 
        : 'Emergency alert sent successfully! Help is on the way.');
      setCurrentStep(4);
      
      // Auto-close after 3 seconds
      setTimeout(() => {
      setDialogOpen(false);
      }, 3000);

    } catch (error) {
      setError((error as Error).message);
      setLocationStatus('error');
      setUserStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    {
      label: 'Confirm Emergency',
      description: 'Verify this is a real emergency situation',
      icon: <WarningIcon />,
    },
    {
      label: 'Get Location',
      description: 'Determining your current location',
      icon: <LocationIcon />,
    },
    {
      label: 'Verify User',
      description: 'Confirming your identity',
      icon: <SecurityIcon />,
    },
    {
      label: 'Send Alert',
      description: 'Transmitting emergency alert',
      icon: <SendIcon />,
    },
    {
      label: 'Complete',
      description: 'Emergency alert sent successfully',
      icon: <CheckCircleIcon />,
    },
  ];

  return (
    <>
      <Button
        variant={variant}
        color="error"
        size={size}
        fullWidth={fullWidth}
        startIcon={<WarningIcon />}
        onClick={() => setDialogOpen(true)}
        sx={{
          fontWeight: 'bold',
          borderRadius: 2,
          textTransform: 'none',
          fontSize: size === 'large' ? '1.1rem' : size === 'small' ? '0.875rem' : '1rem',
          ...(variant === 'contained' && {
            background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
            boxShadow: '0 4px 15px rgba(244, 67, 54, 0.4)',
            '&:hover': {
              background: 'linear-gradient(135deg, #ff5252 0%, #d32f2f 100%)',
              boxShadow: '0 6px 20px rgba(244, 67, 54, 0.6)',
              transform: 'translateY(-2px)',
            },
            transition: 'all 0.3s ease',
          }),
          ...sx,
        }}
      >
        Emergency Alert
      </Button>

      <Dialog 
        open={dialogOpen} 
        onClose={() => !loading && setDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
        TransitionComponent={Slide}
        transitionDuration={400}
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ 
              p: 1, 
              borderRadius: '50%', 
              bgcolor: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <WarningIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 700, mb: 0.5, fontSize: '1.2rem' }}>
                Emergency Alert
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Send emergency alert to operations team
              </Typography>
            </Box>
          </Box>
          <IconButton 
            onClick={() => !loading && setDialogOpen(false)} 
            sx={{ color: 'white' }}
            disabled={loading}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {!loading && !success && (
            <Box sx={{ p: 3 }}>
              {/* Warning Alert */}
              <Alert 
                severity="warning" 
                sx={{ 
                  mb: 3,
                  borderRadius: 2,
                  '& .MuiAlert-icon': { fontSize: 28 },
                }}
                icon={<WarningIcon sx={{ fontSize: 28 }} />}
              >
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  ⚠️ Emergency Alert Confirmation
              </Typography>
                <Typography variant="body1">
                  This will immediately notify the operations team of an emergency situation. 
                  Only use this in case of a real emergency requiring immediate assistance.
              </Typography>
            </Alert>

              {/* Process Steps */}
              <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <InfoIcon color="primary" />
                  What happens next:
                </Typography>
                <Stepper orientation="vertical" activeStep={currentStep}>
                  {steps.map((step, index) => (
                    <Step key={step.label}>
                      <StepLabel 
                        icon={step.icon}
                        sx={{
                          '& .MuiStepLabel-label': {
                            fontWeight: index === currentStep ? 600 : 400,
                          }
                        }}
                      >
                        {step.label}
                      </StepLabel>
                      <StepContent>
                        <Typography variant="body2" color="text.secondary">
                          {step.description}
                        </Typography>
                      </StepContent>
                    </Step>
                  ))}
                </Stepper>
              </Paper>

              {/* Features Card */}
              <Card sx={{ mb: 3, borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SpeedIcon color="primary" />
                    Emergency Response Features
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Chip 
                      label="Instant Notification" 
                      color="primary" 
                      variant="outlined" 
                      size="small"
                    />
                    <Chip 
                      label="GPS Location" 
                      color="primary" 
                      variant="outlined" 
                      size="small"
                    />
                    <Chip 
                      label="24/7 Response" 
                      color="primary" 
                      variant="outlined" 
                      size="small"
                    />
                    <Chip 
                      label="Priority Dispatch" 
                      color="primary" 
                      variant="outlined" 
                      size="small"
                    />
                  </Box>
                </CardContent>
              </Card>

              {/* Notes Field */}
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Emergency Details (Optional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Describe the emergency situation, injuries, or any critical information that will help responders..."
                helperText="Provide specific details about the emergency to help responders prepare appropriately"
                sx={{ mb: 3 }}
              />

              {/* WhatsApp Sharing Section */}
              <Card sx={{ mb: 3, borderRadius: 2, border: '1px solid #e0e0e0' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <MessageIcon color="success" />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Share via WhatsApp
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Choose how you want to share this emergency alert:
                  </Typography>
                  
                  <FormControl component="fieldset" fullWidth>
                    <RadioGroup
                      value={shareToWhatsApp}
                      onChange={(e) => setShareToWhatsApp(e.target.value === 'true')}
                    >
                      <FormControlLabel
                        value={false}
                        control={<Radio />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AppIcon color="primary" sx={{ fontSize: 20 }} />
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                App Only
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Send to OneMove app only
                              </Typography>
                            </Box>
                          </Box>
                        }
                        sx={{
                          mb: 1,
                          p: 1,
                          borderRadius: 1,
                          border: !shareToWhatsApp ? '2px solid #1976d2' : '1px solid #e0e0e0',
                          bgcolor: !shareToWhatsApp ? '#f3f8ff' : 'transparent',
                        }}
                      />
                      <FormControlLabel
                        value={true}
                        control={<Radio />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <MessageIcon color="success" sx={{ fontSize: 20 }} />
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                App + WhatsApp
              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Send to both OneMove app and WhatsApp
              </Typography>
                            </Box>
                          </Box>
                        }
                        sx={{
                          p: 1,
                          borderRadius: 1,
                          border: shareToWhatsApp ? '2px solid #2e7d32' : '1px solid #e0e0e0',
                          bgcolor: shareToWhatsApp ? '#f1f8e9' : 'transparent',
                        }}
                      />
                    </RadioGroup>
                  </FormControl>
                  
                  {shareToWhatsApp && (
                    <Alert severity="info" sx={{ mt: 2, borderRadius: 1 }}>
                      <Typography variant="body2">
                        WhatsApp will open in a new tab with the emergency message pre-filled. 
                        You can then send it to your contacts or groups.
              </Typography>
                    </Alert>
                  )}
                </CardContent>
              </Card>

              {error && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                  {error}
                </Alert>
              )}
            </Box>
          )}

          {/* Loading State */}
          {loading && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress size={60} sx={{ mb: 3, color: '#ff6b6b' }} />
              <Typography variant="h6" sx={{ mb: 2 }}>
                Sending Emergency Alert...
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={(currentStep / 4) * 100} 
                sx={{ mb: 2, height: 8, borderRadius: 4 }}
              />
              <Typography variant="body2" color="text.secondary">
                Step {currentStep} of 4: {steps[currentStep]?.label}
              </Typography>
              
              {/* Step-specific status */}
              {currentStep === 1 && locationStatus === 'getting' && (
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2">Getting your location...</Typography>
                </Box>
              )}
              {currentStep === 2 && userStatus === 'getting' && (
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2">Verifying your identity...</Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Success State */}
          {success && (
            <Fade in timeout={500}>
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Box sx={{ 
                  p: 3, 
                  borderRadius: '50%', 
                  bgcolor: 'success.light', 
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3
                }}>
                  <CheckCircleIcon sx={{ fontSize: 48, color: 'white' }} />
                </Box>
                <Typography variant="h5" sx={{ mb: 2, color: 'success.main', fontWeight: 700 }}>
                  Emergency Alert Sent!
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {success}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  The operations team has been notified and help is on the way.
                </Typography>
          </Box>
            </Fade>
          )}
        </DialogContent>

        {!loading && !success && (
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button 
              onClick={() => setDialogOpen(false)}
              variant="outlined"
              size="large"
              sx={{ borderRadius: 2, px: 3 }}
            >
            Cancel
          </Button>
          <Button
            onClick={handleEmergencyAlert}
            variant="contained"
            color="error"
              size="large"
              startIcon={<WarningIcon />}
              sx={{ 
                borderRadius: 2, 
                px: 4,
                background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #ff5252 0%, #d32f2f 100%)',
                }
              }}
            >
              Send Emergency Alert
          </Button>
        </DialogActions>
        )}
      </Dialog>
    </>
  );
} 